import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useVault } from '../contexts/VaultContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Settings, User, Globe, Volume2, Shield, LogOut, KeyRound, Mail, CreditCard, Download, CheckCircle2, AlertCircle, Lock } from 'lucide-react';
import Layout from '../components/Layout';
import ChangePIN from '../components/ChangePIN';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsPage = () => {
  const { user, logout, updateLanguage } = useAuth();
  const { t, language, setLanguage, supportedLanguages } = useLanguage();
  const { vaultSalt, vaultTestValue, updateVaultPIN, isUnlocked } = useVault();
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [recoveryDialogOpen, setRecoveryDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [changePinOpen, setChangePinOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [settingPin, setSettingPin] = useState(false);
  
  // Recovery options state
  const [recoveryData, setRecoveryData] = useState({
    email: '',
    aadhaar_last4: '',
    driving_license: ''
  });
  const [savingRecovery, setSavingRecovery] = useState(false);
  const [userRecovery, setUserRecovery] = useState(null);
  
  // Export state
  const [exporting, setExporting] = useState(false);

  // Load existing recovery options
  useEffect(() => {
    const fetchRecoveryOptions = async () => {
      try {
        const response = await axios.get(`${API_URL}/auth/recovery-options`);
        setUserRecovery(response.data);
        setRecoveryData({
          email: response.data.email || '',
          aadhaar_last4: response.data.aadhaar_last4 || '',
          driving_license: response.data.driving_license || ''
        });
      } catch (error) {
        // Recovery options not set yet
      }
    };
    fetchRecoveryOptions();
  }, []);

  const handleLanguageChange = async (newLang) => {
    setLanguage(newLang);
    try {
      await updateLanguage(newLang);
      toast.success(t('success'));
    } catch (error) {
      console.error('Failed to update language:', error);
    }
  };

  const handleSetPin = async () => {
    if (!pin || pin.length < 4) {
      toast.error(language === 'hi' ? 'PIN कम से कम 4 अंकों का होना चाहिए' : 'PIN must be at least 4 digits');
      return;
    }
    if (pin !== confirmPin) {
      toast.error(language === 'hi' ? 'PIN मेल नहीं खाता' : 'PINs do not match');
      return;
    }

    setSettingPin(true);
    try {
      await axios.post(`${API_URL}/auth/set-pin?pin=${pin}`);
      toast.success(language === 'hi' ? 'PIN सेट हो गया!' : 'PIN set successfully!');
      setPinDialogOpen(false);
      setPin('');
      setConfirmPin('');
    } catch (error) {
      toast.error(language === 'hi' ? 'PIN सेट करने में त्रुटि' : 'Failed to set PIN');
    } finally {
      setSettingPin(false);
    }
  };

  const handleSaveRecovery = async () => {
    // Validate at least one recovery option
    if (!recoveryData.email && !recoveryData.aadhaar_last4 && !recoveryData.driving_license) {
      toast.error(language === 'hi' ? 'कम से कम एक रिकवरी विकल्प जोड़ें' : 'Add at least one recovery option');
      return;
    }

    // Validate email format
    if (recoveryData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recoveryData.email)) {
      toast.error(language === 'hi' ? 'सही ईमेल पता दर्ज करें' : 'Enter a valid email address');
      return;
    }

    // Validate Aadhaar (last 4 digits)
    if (recoveryData.aadhaar_last4 && !/^\d{4}$/.test(recoveryData.aadhaar_last4)) {
      toast.error(language === 'hi' ? 'आधार के अंतिम 4 अंक दर्ज करें' : 'Enter last 4 digits of Aadhaar');
      return;
    }

    setSavingRecovery(true);
    try {
      await axios.post(`${API_URL}/auth/recovery-options`, recoveryData);
      setUserRecovery(recoveryData);
      toast.success(language === 'hi' ? 'रिकवरी विकल्प सेव हो गए!' : 'Recovery options saved!');
      setRecoveryDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save recovery options');
    } finally {
      setSavingRecovery(false);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/export/all-data`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `EmergencyCard_Data_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(language === 'hi' ? 'डेटा डाउनलोड हो गया!' : 'Data downloaded successfully!');
      setExportDialogOpen(false);
    } catch (error) {
      toast.error(language === 'hi' ? 'डेटा एक्सपोर्ट में त्रुटि' : 'Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const hasRecoveryOptions = userRecovery?.email || userRecovery?.aadhaar_last4 || userRecovery?.driving_license;

  return (
    <Layout>
      <div className="space-y-6 pb-nav max-w-2xl mx-auto">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-slate-100">
            <Settings className="h-6 w-6 text-slate-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0D2340] font-['Manrope']">{t('settings')}</h1>
            <p className="text-slate-500 text-sm">{language === 'hi' ? 'अपना अकाउंट मैनेज करें' : 'Manage your account'}</p>
          </div>
        </div>

        {/* Profile Card */}
        <Card data-testid="profile-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']">
              <User className="h-5 w-5" />
              {language === 'hi' ? 'प्रोफाइल' : 'Profile'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#0D2340] flex items-center justify-center text-white text-2xl font-bold">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div>
                <h3 className="font-semibold text-lg">{user?.name}</h3>
                <p className="text-slate-500">{user?.phone}</p>
                {user?.email && <p className="text-slate-500 text-sm">{user?.email}</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Recovery Card - IMPORTANT */}
        <Card className={`border-2 ${hasRecoveryOptions ? 'border-green-300 bg-green-50/30' : 'border-amber-300 bg-amber-50/30'}`} data-testid="recovery-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']">
              {hasRecoveryOptions ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-amber-600" />
              )}
              {language === 'hi' ? 'अकाउंट रिकवरी' : 'Account Recovery'}
            </CardTitle>
            <CardDescription>
              {language === 'hi' 
                ? 'अगर फोन नंबर बदल जाए तो भी अकाउंट वापस पाएं' 
                : 'Recover your account even if you change your phone number'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {hasRecoveryOptions ? (
              <div className="space-y-2 mb-4">
                <p className="text-sm text-green-700 font-medium">
                  {language === 'hi' ? '✓ रिकवरी विकल्प सेट हैं:' : '✓ Recovery options set:'}
                </p>
                {userRecovery?.email && (
                  <p className="text-sm text-slate-600 flex items-center gap-2">
                    <Mail className="h-4 w-4" /> {userRecovery.email}
                  </p>
                )}
                {userRecovery?.aadhaar_last4 && (
                  <p className="text-sm text-slate-600 flex items-center gap-2">
                    <CreditCard className="h-4 w-4" /> Aadhaar: ****{userRecovery.aadhaar_last4}
                  </p>
                )}
                {userRecovery?.driving_license && (
                  <p className="text-sm text-slate-600 flex items-center gap-2">
                    <CreditCard className="h-4 w-4" /> DL: {userRecovery.driving_license}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-amber-700 mb-4">
                {language === 'hi' 
                  ? '⚠️ रिकवरी विकल्प सेट करें ताकि डेटा कभी न खोएं!' 
                  : '⚠️ Set recovery options to never lose your data!'}
              </p>
            )}
            
            <Dialog open={recoveryDialogOpen} onOpenChange={setRecoveryDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className={hasRecoveryOptions ? 'w-full' : 'w-full bg-amber-500 hover:bg-amber-600 text-white'} 
                  variant={hasRecoveryOptions ? 'outline' : 'default'}
                  data-testid="set-recovery-btn"
                >
                  {hasRecoveryOptions 
                    ? (language === 'hi' ? 'रिकवरी विकल्प बदलें' : 'Update Recovery Options')
                    : (language === 'hi' ? '+ रिकवरी विकल्प जोड़ें' : '+ Add Recovery Options')
                  }
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{language === 'hi' ? 'रिकवरी विकल्प' : 'Recovery Options'}</DialogTitle>
                  <DialogDescription>
                    {language === 'hi' 
                      ? 'कम से कम एक विकल्प जोड़ें। यह आपके अकाउंट को सुरक्षित रखेगा।' 
                      : 'Add at least one option. This keeps your account safe.'}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-5 py-4">
                  {/* Email */}
                  <div>
                    <Label className="flex items-center gap-2 mb-2">
                      <Mail className="h-4 w-4 text-blue-600" />
                      {language === 'hi' ? 'ईमेल पता' : 'Email Address'}
                      <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
                        {language === 'hi' ? 'सुझाव' : 'Recommended'}
                      </span>
                    </Label>
                    <Input
                      type="email"
                      value={recoveryData.email}
                      onChange={(e) => setRecoveryData({...recoveryData, email: e.target.value})}
                      placeholder={language === 'hi' ? 'example@email.com' : 'example@email.com'}
                      className="h-12"
                      data-testid="recovery-email-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {language === 'hi' ? 'OTP ईमेल पर आएगा' : 'OTP will be sent here'}
                    </p>
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-slate-200" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-slate-400">
                        {language === 'hi' ? 'या' : 'OR'}
                      </span>
                    </div>
                  </div>

                  {/* Aadhaar Last 4 */}
                  <div>
                    <Label className="flex items-center gap-2 mb-2">
                      <CreditCard className="h-4 w-4 text-orange-600" />
                      {language === 'hi' ? 'आधार (अंतिम 4 अंक)' : 'Aadhaar (Last 4 digits)'}
                    </Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      maxLength={4}
                      value={recoveryData.aadhaar_last4}
                      onChange={(e) => setRecoveryData({...recoveryData, aadhaar_last4: e.target.value.replace(/\D/g, '')})}
                      placeholder="1234"
                      className="h-12 text-center text-lg tracking-widest"
                      data-testid="recovery-aadhaar-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {language === 'hi' ? 'सुरक्षा के लिए सिर्फ अंतिम 4 अंक' : 'Only last 4 digits for security'}
                    </p>
                  </div>

                  {/* Driving License */}
                  <div>
                    <Label className="flex items-center gap-2 mb-2">
                      <CreditCard className="h-4 w-4 text-purple-600" />
                      {language === 'hi' ? 'ड्राइविंग लाइसेंस नंबर' : 'Driving License Number'}
                    </Label>
                    <Input
                      type="text"
                      value={recoveryData.driving_license}
                      onChange={(e) => setRecoveryData({...recoveryData, driving_license: e.target.value.toUpperCase()})}
                      placeholder={language === 'hi' ? 'जैसे MH12-20190001234' : 'e.g. MH12-20190001234'}
                      className="h-12"
                      data-testid="recovery-dl-input"
                    />
                  </div>

                  <Button
                    onClick={handleSaveRecovery}
                    disabled={savingRecovery}
                    className="w-full h-12 bg-[#0D2340]"
                    data-testid="save-recovery-btn"
                  >
                    {savingRecovery 
                      ? (language === 'hi' ? 'सेव हो रहा है...' : 'Saving...') 
                      : (language === 'hi' ? 'सेव करें' : 'Save')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        {/* Export Data Card */}
        <Card data-testid="export-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']">
              <Download className="h-5 w-5 text-blue-600" />
              {language === 'hi' ? 'डेटा एक्सपोर्ट' : 'Export My Data'}
            </CardTitle>
            <CardDescription>
              {language === 'hi' 
                ? 'अपना सारा स्वास्थ्य डेटा डाउनलोड करें' 
                : 'Download all your health data'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full" data-testid="export-data-btn">
                  <Download className="h-4 w-4 mr-2" />
                  {language === 'hi' ? 'डेटा डाउनलोड करें' : 'Download Data'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{language === 'hi' ? 'डेटा एक्सपोर्ट' : 'Export Data'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <p className="text-sm text-slate-600">
                    {language === 'hi' 
                      ? 'इसमें शामिल होगा:' 
                      : 'This will include:'}
                  </p>
                  <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
                    <li>{language === 'hi' ? 'मेडिकल हिस्ट्री' : 'Medical History'}</li>
                    <li>{language === 'hi' ? 'प्रिस्क्रिप्शन' : 'Prescriptions'}</li>
                    <li>{language === 'hi' ? 'लैब रिपोर्ट्स' : 'Lab Reports'}</li>
                    <li>{language === 'hi' ? 'हेल्थ डायरी' : 'Health Diary'}</li>
                    <li>{language === 'hi' ? 'रिमाइंडर्स' : 'Reminders'}</li>
                  </ul>
                  <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-700">
                    {language === 'hi' 
                      ? '⚠️ इस फाइल को सुरक्षित रखें - इसमें आपकी निजी जानकारी है।' 
                      : '⚠️ Keep this file safe - it contains your private health information.'}
                  </div>
                  <Button
                    onClick={handleExportData}
                    disabled={exporting}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="confirm-export-btn"
                  >
                    {exporting 
                      ? (language === 'hi' ? 'डाउनलोड हो रहा है...' : 'Downloading...') 
                      : (language === 'hi' ? 'डाउनलोड शुरू करें' : 'Start Download')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        {/* Multi-Device PIN Card */}
        <Card className="border-[#FFB300]" data-testid="multi-device-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']">
              <KeyRound className="h-5 w-5 text-[#FFB300]" />
              {language === 'hi' ? 'लॉगिन PIN' : 'Login PIN'}
            </CardTitle>
            <CardDescription>
              {language === 'hi' 
                ? 'PIN से किसी भी डिवाइस पर लॉगिन करें' 
                : 'Login from any device with PIN'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
              <DialogTrigger asChild>
                <Button className="w-full bg-[#FFB300] text-slate-900 hover:bg-[#FFB300]/90" data-testid="set-pin-btn">
                  <KeyRound className="h-5 w-5 mr-2" />
                  {language === 'hi' ? 'PIN सेट करें' : 'Set Login PIN'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{language === 'hi' ? 'लॉगिन PIN सेट करें' : 'Set Your Login PIN'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-slate-500">
                    {language === 'hi' 
                      ? 'यह PIN आपको किसी भी डिवाइस से लॉगिन करने देगा।' 
                      : 'This PIN lets you login from any device.'}
                  </p>
                  <div>
                    <Label>{language === 'hi' ? 'PIN बनाएं (4-6 अंक)' : 'Create PIN (4-6 digits)'}</Label>
                    <Input
                      type="password"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={pin}
                      onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                      className="h-14 text-2xl text-center tracking-widest"
                      placeholder="••••"
                      data-testid="settings-pin-input"
                    />
                  </div>
                  <div>
                    <Label>{language === 'hi' ? 'PIN दोबारा डालें' : 'Confirm PIN'}</Label>
                    <Input
                      type="password"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={confirmPin}
                      onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                      className="h-14 text-2xl text-center tracking-widest"
                      placeholder="••••"
                      data-testid="settings-confirm-pin-input"
                    />
                  </div>
                  <Button
                    onClick={handleSetPin}
                    disabled={settingPin}
                    className="w-full bg-[#0D2340]"
                    data-testid="save-pin-btn"
                  >
                    {settingPin ? '...' : (language === 'hi' ? 'PIN सेव करें' : 'Save PIN')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>

        {/* Language Card */}
        <Card data-testid="language-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']">
              <Globe className="h-5 w-5" />
              {language === 'hi' ? 'भाषा' : 'Language'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Primary Languages (English & Hindi) */}
            <div className="flex gap-4 mb-4">
              <Button
                variant={language === 'en' ? 'default' : 'outline'}
                onClick={() => handleLanguageChange('en')}
                className={language === 'en' ? 'bg-[#0D2340] flex-1' : 'flex-1'}
                data-testid="lang-en-btn"
              >
                🇬🇧 English
              </Button>
              <Button
                variant={language === 'hi' ? 'default' : 'outline'}
                onClick={() => handleLanguageChange('hi')}
                className={language === 'hi' ? 'bg-[#0D2340] flex-1' : 'flex-1'}
                data-testid="lang-hi-btn"
              >
                🇮🇳 हिंदी
              </Button>
            </div>
            
            {/* More Languages - Collapsible */}
            <details className="text-sm">
              <summary className="cursor-pointer text-slate-500 hover:text-slate-700 py-2">
                {language === 'hi' ? 'और भाषाएं देखें...' : 'More languages...'}
              </summary>
              <div className="grid grid-cols-2 gap-2 mt-3">
                {supportedLanguages
                  .filter(lang => lang.code !== 'en' && lang.code !== 'hi')
                  .map(lang => (
                    <Button
                      key={lang.code}
                      variant={language === lang.code ? 'default' : 'outline'}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`text-sm ${language === lang.code ? 'bg-[#0D2340]' : ''}`}
                      data-testid={`lang-${lang.code}-btn`}
                    >
                      {lang.flag} {lang.name}
                    </Button>
                  ))}
              </div>
            </details>
          </CardContent>
        </Card>

        {/* Security Info Card */}
        <Card data-testid="security-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Manrope']">
              <Shield className="h-5 w-5" />
              {language === 'hi' ? 'सुरक्षा' : 'Security'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-[#0D2340]/5 p-4 rounded-lg">
              <div className="grid grid-cols-3 gap-3 text-center text-sm mb-3">
                <div className="bg-white p-2 rounded shadow-sm">
                  <p className="font-semibold text-green-600">AES-256</p>
                  <p className="text-xs text-slate-500">{language === 'hi' ? 'एन्क्रिप्शन' : 'Encryption'}</p>
                </div>
                <div className="bg-white p-2 rounded shadow-sm">
                  <p className="font-semibold text-blue-600">SHA-256</p>
                  <p className="text-xs text-slate-500">{language === 'hi' ? 'ब्लॉकचेन' : 'Blockchain'}</p>
                </div>
                <div className="bg-white p-2 rounded shadow-sm">
                  <p className="font-semibold text-purple-600">OTP</p>
                  <p className="text-xs text-slate-500">{language === 'hi' ? 'वेरिफिकेशन' : 'Verification'}</p>
                </div>
              </div>
              <p className="text-sm text-slate-600">
                {language === 'hi' 
                  ? 'आपका सारा डेटा एन्क्रिप्टेड और सुरक्षित है।' 
                  : 'All your data is encrypted and secure.'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Vault PIN Card - Change PIN */}
        {isUnlocked && (
          <Card className="border-2 border-green-200 bg-green-50/30" data-testid="vault-pin-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Manrope']">
                <Lock className="h-5 w-5 text-green-600" />
                {language === 'hi' ? 'वॉल्ट PIN' : 'Vault PIN'}
              </CardTitle>
              <CardDescription>
                {language === 'hi' 
                  ? 'आपका हेल्थ डेटा इस PIN से एन्क्रिप्टेड है' 
                  : 'Your health data is encrypted with this PIN'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="bg-green-100 p-3 rounded-lg">
                <p className="text-sm text-green-800 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  {language === 'hi' 
                    ? 'वॉल्ट अनलॉक है • हम आपका डेटा नहीं देख सकते' 
                    : 'Vault unlocked • We cannot see your data'}
                </p>
              </div>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setChangePinOpen(true)}
                data-testid="change-vault-pin-btn"
              >
                <KeyRound className="h-4 w-4 mr-2" />
                {language === 'hi' ? 'PIN बदलें' : 'Change PIN'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Change PIN Dialog */}
        <ChangePIN
          isOpen={changePinOpen}
          onClose={() => setChangePinOpen(false)}
          currentSalt={vaultSalt}
          testValue={vaultTestValue}
          onPINChanged={async ({ salt, testValue, key }) => {
            await updateVaultPIN(salt, testValue, key);
          }}
        />

        {/* Logout */}
        <Card className="border-red-200">
          <CardContent className="p-4">
            <Button
              variant="destructive"
              onClick={logout}
              className="w-full"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {t('logout')}
            </Button>
          </CardContent>
        </Card>

        {/* Legal Links */}
        <div className="text-center text-sm text-slate-500 space-y-2 pt-4">
          <div className="flex justify-center gap-4">
            <a href="/privacy-policy" className="hover:text-[#0D2340] underline">
              {language === 'hi' ? 'गोपनीयता नीति' : 'Privacy Policy'}
            </a>
            <span>•</span>
            <a href="/terms" className="hover:text-[#0D2340] underline">
              {language === 'hi' ? 'सेवा की शर्तें' : 'Terms of Service'}
            </a>
          </div>
          <p className="text-xs text-slate-400">
            EmergencyCard Health v1.0 • Made with ❤️
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default SettingsPage;
